/*
Copyright 2022 Fondazione Bruno Kessler
Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
except in compliance with the Licence.

You may obtain a copy of the Licence at:
http://ec.europa.eu/idabc/eupl.html

Unless required by applicable law or agreed to in writing, software distributed under 
the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF 
ANY KIND, either express or implied. See the Licence for the specific language 
governing permissions and limitations under the Licence.
*/
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import {DEFAULT_ARRUOLATI, PAGE_SIZE} from "@app/models/constants/app-constants";
import {Patient} from "@app/modules/doctor-panel/models/patient";
import {DoctorPanelActions} from "@app/modules/doctor-panel/store/doctor-panel.actions";
import {DoctorPanelState} from "@app/modules/doctor-panel/store/doctor-panel.state";
import {CommonState} from "@app/store/common/common.state";
import {ModalController, ToastController} from "@ionic/angular";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {GroupMultiPatient, QuestionnairesService, VideoChatService} from "@trec/api";
import {validPatientValidator} from "@video-meeting/validators/valid-patient.validator";
import {Observable, Subscription} from "rxjs";
import {map, startWith} from "rxjs/operators";
@Component({
  selector: "c3-modal-detail-multi-patient",
  templateUrl: "./modal-detail-multi-patient.component.html",
  styleUrls: ["./modal-detail-multi-patient.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ModalDetailMultiPatientComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() mode: "televisita" | "survey" | "default" = "default";
  @Input() id: string | number | null = null;
  @Select(DoctorPanelState.patientsList) patientsList$: Observable<Map<string, Patient> | null>;

  @Select(hasActionsExecuting([DoctorPanelActions.FetchPatientsList]))
  loadingPatList$: Observable<boolean>;

  readonly form: UntypedFormGroup;
  get patientIdControl(): UntypedFormControl {
    return this.form.get("patientId") as UntypedFormControl;
  }

  @Input() name: string | null = null;
  patientList: Map<string, Patient> = new Map();
  filteredPatientList: Map<string, Patient> = new Map();
  denominazione: string;
  patientIdSelected: any;
  physician;

  multiPatientList: any;

  private readonly subscr = new Subscription();

  readonly patientDisplayFn = (patientId: string | null) => {
    const patient = this.patientList.get(patientId ?? "");
    return patient
      ? `${patient.personalData?.name ?? ""} ${patient.personalData?.surname ?? ""} (${patient.personalData?.fiscalCode ?? ""
      })`
      : null;
  };

  creationValue: boolean = true;

  constructor(
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private modalController: ModalController,
    private toastController: ToastController,
    private store: Store,
    private videoChatService: VideoChatService,
    private questionnairesService: QuestionnairesService,
  ) {
    this.form = this.fb.group({
      patientId: this.fb.control(null, {
        validators: [Validators.required],
        asyncValidators: [validPatientValidator(this.patientsList$)],
      }),
    });
  }

  ngOnInit(): void {
    this.checkSelection();
    const structureId = this.store.selectSnapshot(CommonState.currentStructureId);

    this.store
      .dispatch(
        new DoctorPanelActions.FetchPatientsList({
          page: 0,
          tipo: DEFAULT_ARRUOLATI,
          filtro: null,
          pageSize: PAGE_SIZE,
          idStruttura: structureId || "",
        }),
      )
      .subscribe(
        () => {
          const chosenPatientId = this.patientIdControl.value;
          if (chosenPatientId) {
            this.patientIdControl.patchValue(null);
            this.patientIdControl.patchValue(chosenPatientId);
          }
        },
        () => { },
      );

    this.subscr.add(
      this.patientsList$.subscribe((patientList) => {
        this.patientList = patientList ?? new Map();
      }),
    );
    this.subscr.add(
      this.patientIdControl.valueChanges
        .pipe(
          startWith(""),
          map((value) => this.filterPatients(value)), // Search for patients
        )
        .subscribe((filteredPatients) => {
          // Patient search field changed
          this.filteredPatientList = filteredPatients;
          this.cd.markForCheck();
        }),
    );
  }

  ngAfterViewInit() {
    // Check if some data ha been passed -> if yes -> update form
    this.updateWithPassedData();
  }

  private updateWithPassedData() {
    if (this.patientIdSelected) {
      this.patientIdControl.patchValue(this.patientIdSelected);
      this.patientIdControl.disable();
    }
  }

  selection(paziente: any) {
    //console.log(paziente);
    this.patientIdSelected = paziente.key;
    this.denominazione =
      paziente.value.personalData.surname + " " + paziente.value.personalData.name;
    let isPresent = false;
    for (let i = 0; i < this.multiPatientList.length; i++) {
      if (this.patientIdSelected === this.multiPatientList[i].trecUserId) {
        isPresent = true;
      }
    }

    if (!isPresent) {
      this.multiPatientList.push({
        name: paziente.value.personalData.name,
        surname: paziente.value.personalData.surname,
        trecUserId: this.patientIdSelected,
      });
      this.checkSelection();
    } else {
      this.errorPatientIsPresent("Paziente già presente");
    }
    this.patientIdControl.reset();
    //console.log(this.multiPatientList);
  }

  searchPatients() {
    const structureId = this.store.selectSnapshot(CommonState.currentStructureId);
    if (this.patientIdControl.value.length >= 3) {
      this.store.dispatch(
        new DoctorPanelActions.FetchPatientsList({
          page: 0,
          tipo: DEFAULT_ARRUOLATI,
          filtro: this.patientIdControl.value,
          pageSize: PAGE_SIZE,
          idStruttura: structureId || "",
        }),
      );
    }
    this.store.select(DoctorPanelState.patientsList).subscribe((patientsList) => {
      this.filteredPatientList = patientsList ? patientsList : new Map();
      this.cd.detectChanges();
    });
  }

  private filterPatients(value: string | null): Map<string, Patient> {
    const filterValue = value?.toLowerCase() ?? "";

    const filtered = new Map<string, Patient>();

    this.patientList.forEach((patient, patientId) => {
      // eslint-disable-next-line max-len
      if (
        `${patient.personalData?.name} ${patient.personalData?.surname} ${patient.personalData?.fiscalCode}`
          .toLowerCase()
          .includes(filterValue)
      ) {
        filtered.set(patientId, patient);
      }
    });

    return filtered;
  }

  elimina(patient) {
    for (let i = 0; i < this.multiPatientList.length; i++) {
      if (this.multiPatientList[i].trecUserId === patient.trecUserId) {
        this.multiPatientList.splice(i, 1);
      }
    }
    this.checkSelection();
  }

  updateGruppo() {
    let participants = this.multiPatientList;
    let groupMultiPatient: GroupMultiPatient = {
      groupName: this.name!,
      physician: this.physician,
      participantsGroup: participants,
    };

    let groupMultiPatientSurvey: any = {
      id: this.id ?? null,
      name: this.name,
      structureId: this.store.selectSnapshot(CommonState.currentStructureId),
      users: participants,
    };
    if (this.mode === "televisita" || this.mode === "default") {
      this.videoChatService.updateMultiPatient(groupMultiPatient).subscribe(
        (data) => {
          this.successToast();
        },
        (err) => {
          console.error(err);
          this.errorToast(err);
        },
      );
    }

    if (this.mode === "survey" || this.mode === "default") {
      this.questionnairesService.updateGroup(groupMultiPatientSurvey).subscribe(
        (data) => {
          this.successToast();
        },
        (err) => {
          console.error(err);
          this.errorToast(err);
        },
      );
    }
  }

  closeModal() {
    const newGroup = {
      team: this.multiPatientList,
      name: this.name,
    };
    this.modalController.dismiss(newGroup);
  }

  async errorToast(err: any) {
    const toast = await this.toastController.create({
      header: "Errore",
      message: err,
      duration: 3000,
      color: "danger",
      position: "bottom",
    });
    toast.present();
    this.closeModal();
  }

  async errorPatientIsPresent(msg: string) {
    const toast = await this.toastController.create({
      header: "Errore",
      message: msg,
      duration: 3000,
      color: "danger",
      position: "bottom",
    });
    toast.present();
  }

  async successToast() {
    const toast = await this.toastController.create({
      message: "Gruppo creato con successo",
      duration: 3000,
      color: "success",
      position: "bottom",
    });
    toast.present();
    this.closeModal();
  }

  checkSelection() {
    if (
      (this.multiPatientList.length > 1 && this.mode === "televisita") ||
      (this.multiPatientList.length > 0 && this.mode === "survey")
    ) {
      this.creationValue = false;
    } else {
      this.creationValue = true;
    }
  }

  ngOnDestroy() {
    this.subscr.unsubscribe();
  }
}
